import React, { useState, useEffect, useRef, useCallback } from 'react'; 
import { applicationsMetaService } from '../../../../components/api/applicationsMetaService';

interface ApplicationData {
  id: string;
  title: string;
  description: string;
  tags: string;
  status: number;
  icon: string;
  url: string;
  spacode: string;
  version: string;
  createdBy: string;
  tenantId: string;
  mappedAgentsCount: string;
  category: string;
}

const AppViewer: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [appData, setAppData] = useState<ApplicationData | null>(null);
  const [spacode, setSpacode] = useState<string>('');
  const [pendingHtmlCode, setPendingHtmlCode] = useState<string | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Extract spacode from URL
    const extractSpacodeFromUrl = () => {
      const path = window.location.pathname;
      const segments = path.split('/').filter(segment => segment.length > 0);
      const lastSegment = segments[segments.length - 1];
      return lastSegment || '';
    };

    const extractedSpacode = extractSpacodeFromUrl();
    setSpacode(extractedSpacode);

    if (!extractedSpacode) {
      setError('No spacode found in URL. Expected format: /agent/[spacode]');
      return;
    }

    loadApplication(extractedSpacode);
  }, []);

  // Effect to handle rendering when both iframe ref and HTML code are ready
  useEffect(() => {
    if (pendingHtmlCode && iframeRef.current) {
      renderApplication(pendingHtmlCode);
      setPendingHtmlCode(null);
    }
  }, [pendingHtmlCode, iframeRef.current]);

  const loadApplication = async (currentSpacode: string) => {
    try {
      setLoading(true);
      setError(null);

      const response = await applicationsMetaService.GetBySpacode(currentSpacode);
      
      if (!response || !Array.isArray(response) || response.length === 0) {
        throw new Error('No application found for the provided spacode');
      }

      const applicationData = response[0] as ApplicationData;
      
      if (!applicationData.spacode) {
        throw new Error('Application code not found');
      }

      setAppData(applicationData);
      
      // Instead of calling renderApplication immediately, set pending HTML code
      // The useEffect above will handle the actual rendering when iframe is ready
      if (iframeRef.current) {
        renderApplication(applicationData.spacode);
      } else {
        setPendingHtmlCode(applicationData.spacode);
      }

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load application';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const renderApplication = useCallback((htmlCode: string) => {
    if (!iframeRef.current) {
      // Store the HTML code to render later when iframe is ready
      setPendingHtmlCode(htmlCode);
      return;
    }

    const iframe = iframeRef.current;
    
    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
      
      if (!iframeDoc) {
        setError('Failed to access iframe document');
        return;
      }

      try {
        // Clear iframe content
        iframeDoc.open();
        iframeDoc.write('');
        iframeDoc.close();

        // Write the HTML code to iframe
        iframeDoc.open();
        iframeDoc.write(htmlCode);
        iframeDoc.close();

        // Auto-resize iframe to content
        const resizeIframe = () => {
          try {
            const body = iframeDoc.body;
            const html = iframeDoc.documentElement;
            const height = Math.max(
              body?.scrollHeight || 0,
              body?.offsetHeight || 0,
              html?.clientHeight || 0,
              html?.scrollHeight || 0,
              html?.offsetHeight || 0
            );
            iframe.style.minHeight = '100vh';//Math.max(height, 400) + 'px';
          } catch (e) {
            // Cross-origin or other access issues
            iframe.style.height = '600px';
          }
        };

        // Multiple resize attempts to handle dynamic content
        setTimeout(resizeIframe, 100);
        setTimeout(resizeIframe, 500);
        setTimeout(resizeIframe, 1000);
        
        iframe.onload = () => {
          resizeIframe();
          // Also resize after a short delay in case content loads asynchronously
          setTimeout(resizeIframe, 200);
        };

      } catch (error) {
        console.error('Error rendering application:', error);
        setError('Failed to render application content');
      }
    });
  }, []);

  // Callback ref to ensure we know when iframe is mounted
  const iframeCallbackRef = useCallback((node: HTMLIFrameElement | null) => {
    iframeRef.current = node;
    if (node && pendingHtmlCode) {
      // Iframe is now available and we have pending content to render
      renderApplication(pendingHtmlCode);
      setPendingHtmlCode(null);
    }
  }, [pendingHtmlCode, renderApplication]);

  const refreshApplication = () => {
    if (spacode) {
      setPendingHtmlCode(null);
      loadApplication(spacode);
    }
  };

 if (loading) {
  return (
    <div className="app-viewer-loading" style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      height: '100vh',
      flexDirection: 'column',
      gap: '16px'
    }}>
      {/* Spinner Loader */}
      <div style={{
        width: '48px',
        height: '48px',
        border: '4px solid #f3f3f3',
        borderTop: '4px solid #3498db',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite'
      }} />
      
      <div style={{ fontWeight: '500', color: '#333' }}>Loading application...</div>

      {/* CSS Animation */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

  if (error) {
    return (
      <div className="app-viewer-error" style={{ 
        padding: '20px', 
        border: '1px solid #ff6b6b', 
        borderRadius: '8px', 
        backgroundColor: '#ffe0e0',
        color: '#d63031'
      }}>
        <h3>Error Loading Application</h3>
        <p>{error}</p>
        <div style={{ marginTop: '10px' }}>
          <button 
            onClick={refreshApplication}
            style={{
              padding: '8px 16px',
              backgroundColor: '#d63031',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Retry
          </button>
        </div>
        <div style={{ fontSize: '12px', marginTop: '10px', color: '#666' }}>
          URL: {window.location.href}
        </div>
        <div style={{ fontSize: '12px', color: '#666' }}>
          Spacode: {spacode}
        </div>
      </div>
    );
  }

  return (
    <div className="app-viewer-container" ref={containerRef} style={{ width: '100%', position: 'relative' }}>
      {loading && (
        <div style={{
          position: 'absolute',
          top: 0, left: 0, right: 0, bottom: 0,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          background: 'rgba(255,255,255,0.8)',
          zIndex: 10
        }}>
          <div>Loading application...</div>
        </div>
      )}

      <div className="app-content" style={{ 
        border: '1px solid #ddd', 
        borderRadius: '8px',
        overflow: 'hidden'
      }}>
        <iframe
          ref={iframeCallbackRef}
          style={{
            width: '100%',
            minHeight: '400px',
            border: 'none',
            display: 'block'
          }}
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals"
          title={`App: ${appData?.title || spacode}`}
        />
      </div>
    </div>
  );
};

export default AppViewer;